import { observer } from 'mobx-react-lite';
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import dayjs from 'dayjs';

import { PaginationMeasStore } from '../../../stores/pagination-meas-store';
import { toDepth } from '../../../utils/depth';
import { makeSortLabel, PAGE_SIZE_OPTIONS, useMeasTableHandlers } from './table-utils';

interface Props {
    store: PaginationMeasStore;
}

const DetailedTable = observer(({ store }: Props) => {
    const { items, loading, size, page, pageSize, sorting } = store;

    const { handlePageChange, handleRowsPerPageChange, handleSort } = useMeasTableHandlers(store);
    const sortLabel = makeSortLabel(sorting, handleSort);

    const rows = items.flatMap((m, measIdx) =>
        m.values.length === 0 ? [] : m.values.map((val, i) => ({ m, measIdx, i, val })),
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <TableContainer component={Paper} elevation={0} sx={{ flex: 1, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {sortLabel('device', 'Устройство')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {sortLabel('sensor', 'Датчик')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {sortLabel('measure', 'Время')}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                №
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                Глубина, м
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                Показание
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Нет данных
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map(({ m, measIdx, i, val }) => (
                                <TableRow
                                    key={`${measIdx}-${i}`}
                                    hover
                                    sx={{
                                        cursor: 'default',
                                        ...(i === 0 && measIdx > 0 ? { '& > td': { borderTopWidth: 2 } } : {}),
                                    }}
                                >
                                    {i === 0 ? (
                                        <>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {m.overdue && (
                                                        <Tooltip title="Историческое" placement="right">
                                                            <SaveIcon
                                                                sx={{
                                                                    fontSize: 14,
                                                                    color: 'warning.main',
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    <Typography variant="body2">{m.device}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{m.sensor}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Typography variant="body2">
                                                    {dayjs(m.measure).format('DD.MM.YYYY HH:mm')}
                                                </Typography>
                                            </TableCell>
                                        </>
                                    ) : (
                                        <>
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />
                                        </>
                                    )}
                                    <TableCell align="right">
                                        <Typography variant="body2">{i + 1}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography variant="body2">
                                            {i < m.depths.length ? toDepth(m, i).toFixed(2) : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography variant="body2">{val.toFixed(2)}</Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                <TablePagination
                    component="div"
                    count={size}
                    page={page}
                    rowsPerPage={pageSize}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    labelRowsPerPage="Замеров:"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
                />
            </Box>
        </Box>
    );
});

export default DetailedTable;
